import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import {
  ChevronLeft, ChevronRight, Check, Globe, Palette, FileText,
  DollarSign, Server, Sparkles, Zap, Building2, MapPin,
  Languages, Mic, Scale, BookOpen,
  LayoutGrid, Columns3, PanelLeft, Layers, Grid3X3, AlignLeft,
  Tags, Clock, Link2, Search, Eye, Rocket,
  CreditCard, Megaphone, Mail, Users, Lock,
  Wifi, Shield as ShieldIcon, Cloud, Database,
  Brain, AlertTriangle, ThumbsUp, MessageSquare,
  Timer, Filter, Bell, RefreshCw, Share2, Repeat,
  Upload, X, Image as ImageIcon, Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { templateModels } from "@/lib/templates";

// ─── Language database ───────────────────────────────────
const allLanguages = [
  { code: "en", name: "English", flag: "🇬🇧", regions: ["Global", "North America", "Europe", "Asia-Pacific", "Africa"] },
  { code: "fr", name: "Français", flag: "🇫🇷", regions: ["Europe", "Africa", "Global"] },
  { code: "ar", name: "العربية", flag: "🇸🇦", regions: ["Middle East", "Africa", "Global"] },
  { code: "es", name: "Español", flag: "🇪🇸", regions: ["Europe", "Latin America", "North America", "Global"] },
  { code: "de", name: "Deutsch", flag: "🇩🇪", regions: ["Europe"] },
  { code: "it", name: "Italiano", flag: "🇮🇹", regions: ["Europe"] },
  { code: "pt", name: "Português", flag: "🇧🇷", regions: ["Latin America", "Europe", "Africa"] },
  { code: "zh", name: "中文", flag: "🇨🇳", regions: ["Asia-Pacific", "Global"] },
  { code: "ja", name: "日本語", flag: "🇯🇵", regions: ["Asia-Pacific"] },
  { code: "ko", name: "한국어", flag: "🇰🇷", regions: ["Asia-Pacific"] },
  { code: "hi", name: "हिन्दी", flag: "🇮🇳", regions: ["Asia-Pacific"] },
  { code: "tr", name: "Türkçe", flag: "🇹🇷", regions: ["Europe", "Middle East"] },
  { code: "ru", name: "Русский", flag: "🇷🇺", regions: ["Europe", "Asia-Pacific"] },
  { code: "nl", name: "Nederlands", flag: "🇳🇱", regions: ["Europe"] },
  { code: "pl", name: "Polski", flag: "🇵🇱", regions: ["Europe"] },
  { code: "sv", name: "Svenska", flag: "🇸🇪", regions: ["Europe"] },
  { code: "da", name: "Dansk", flag: "🇩🇰", regions: ["Europe"] },
  { code: "no", name: "Norsk", flag: "🇳🇴", regions: ["Europe"] },
  { code: "fi", name: "Suomi", flag: "🇫🇮", regions: ["Europe"] },
  { code: "el", name: "Ελληνικά", flag: "🇬🇷", regions: ["Europe"] },
  { code: "th", name: "ไทย", flag: "🇹🇭", regions: ["Asia-Pacific"] },
  { code: "vi", name: "Tiếng Việt", flag: "🇻🇳", regions: ["Asia-Pacific"] },
  { code: "id", name: "Bahasa Indonesia", flag: "🇮🇩", regions: ["Asia-Pacific"] },
  { code: "ms", name: "Bahasa Melayu", flag: "🇲🇾", regions: ["Asia-Pacific"] },
  { code: "sw", name: "Kiswahili", flag: "🇰🇪", regions: ["Africa"] },
  { code: "am", name: "አማርኛ", flag: "🇪🇹", regions: ["Africa"] },
  { code: "he", name: "עברית", flag: "🇮🇱", regions: ["Middle East"] },
  { code: "ur", name: "اردو", flag: "🇵🇰", regions: ["Asia-Pacific", "Middle East"] },
];

// ─── Step definitions ────────────────────────────────────
const steps = [
  { id: 1, title: "Site Identity", icon: Globe, desc: "Name, niche & language" },
  { id: 2, title: "Design", icon: Palette, desc: "Theme & layout" },
  { id: 3, title: "Content Strategy", icon: FileText, desc: "Categories & rules" },
  { id: 4, title: "Monetization", icon: DollarSign, desc: "Revenue model" },
  { id: 5, title: "Technical Setup", icon: Server, desc: "Domain & hosting" },
  { id: 6, title: "AI Rules", icon: Sparkles, desc: "Writing & safety" },
  { id: 7, title: "Automation", icon: Zap, desc: "Workflows & publishing" },
  { id: 8, title: "Review & Launch", icon: Eye, desc: "Summary & confirmation" },
];

// ─── Shared input components ─────────────────────────────
function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 block">{label}</label>
      {children}
      {error && <p className="text-[11px] text-destructive mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p>}
    </div>
  );
}

function TextInput({ placeholder, value, onChange, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground transition-colors"
      {...props}
    />
  );
}

function TextArea({ placeholder, rows = 3, value, onChange }: { placeholder: string; rows?: number; value: string; onChange: (v: string) => void }) {
  return (
    <textarea
      placeholder={placeholder}
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground resize-none transition-colors"
    />
  );
}

function SelectInput({ options, value, onChange }: { options: string[]; value: string; onChange: (val: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function ChipSelect({ options, selected, onToggle, multi = false }: {
  options: { id: string; label: string; icon?: React.ElementType; desc?: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  multi?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const active = selected.includes(o.id);
        const Icon = o.icon;
        return (
          <button
            key={o.id}
            onClick={() => onToggle(o.id)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all border",
              active
                ? "bg-primary/10 border-primary/40 text-primary"
                : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40"
            )}
          >
            {Icon && <Icon className="h-4 w-4" />}
            <span className="font-medium">{o.label}</span>
            {active && <Check className="h-3 w-3 ml-1" />}
          </button>
        );
      })}
    </div>
  );
}

function CardSelect({ options, selected, onSelect }: {
  options: { id: string; label: string; icon: React.ElementType; desc: string }[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      {options.map(o => {
        const active = selected === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onSelect(o.id)}
            className={cn(
              "flex flex-col items-start rounded-lg border p-4 text-left transition-all",
              active
                ? "bg-primary/8 border-primary/40 shadow-sm shadow-primary/10"
                : "bg-card border-border hover:border-muted-foreground/40"
            )}
          >
            <o.icon className={cn("h-5 w-5 mb-2", active ? "text-primary" : "text-muted-foreground")} />
            <span className={cn("text-sm font-medium", active ? "text-primary" : "text-foreground")}>{o.label}</span>
            <span className="text-xs text-muted-foreground mt-0.5">{o.desc}</span>
          </button>
        );
      })}
    </div>
  );
}

function ToggleRow({ label, desc, on, onToggle }: { label: string; desc: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 hover:border-muted-foreground/30 transition-colors">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <button
        onClick={() => onToggle(!on)}
        className={cn(
          "relative h-6 w-11 rounded-full transition-colors shrink-0 ml-4",
          on ? "bg-primary" : "bg-muted"
        )}
      >
        <span className={cn(
          "absolute top-0.5 h-5 w-5 rounded-full bg-foreground transition-transform",
          on ? "left-[22px]" : "left-0.5"
        )} />
      </button>
    </div>
  );
}

// ─── Full form state type ────────────────────────────────
type WizardData = {
  // Step 1: Identity
  siteName: string;
  siteSlug: string;
  siteDescription: string;
  siteNiche: string;
  siteTagline: string;
  primaryLang: string;
  secondaryLangs: string[];
  region: string;
  logoPreview: string | null;
  logoFile: File | null;
  editorialTone: string;
  legalEntity: string;
  publishingModel: string;
  // Step 2: Design
  theme: string;
  layout: string;
  templateId: string | null;
  colorPalette: string[];
  typography: string;
  cardStyle: string;
  headerStyle: string;
  homepageStructure: string;
  // Step 3: Content
  categories: string[];
  articleTypes: string[];
  publishingFrequency: string;
  evergreenRatio: number;
  originalityThreshold: number;
  rewriteIntensity: number;
  internalLinking: boolean;
  autoSeoTags: boolean;
  humanReviewRequired: boolean;
  // Step 4: Monetization
  displayAds: boolean;
  affiliateLinks: boolean;
  sponsoredContent: boolean;
  newsletterMonetization: boolean;
  premiumSubscription: boolean;
  leadGeneration: boolean;
  adDensity: string;
  affiliateNetwork: string;
  ctaPlacements: string[];
  // Step 5: Technical
  siteDomain: string;
  subdomain: string;
  hostingPlan: string;
  autoProvision: boolean;
  dbType: string;
  hostingProvider: string;
  cmsConnection: string;
  cdn: string;
  sslCertificate: string;
  serverRegion: string;
  autoDeployOnPublish: boolean;
  stagingEnvironment: boolean;
  emailSenderDomain: boolean;
  // Step 6: AI
  writingModel: string;
  translationModel: string;
  imageGeneration: string;
  toneStyleRules: string;
  factConsistency: boolean;
  biasDetection: boolean;
  medicalLegalDisclaimer: boolean;
  contentSafetyFilter: boolean;
  bannedTopics: string;
  bannedClaims: string;
  requiredAttribution: string;
  titleGeneration: string;
  metaDescriptionStyle: string;
  // Step 7: Automation
  pollingFrequency: string;
  maxArticlesPerPoll: string;
  autoApproveHighTrust: boolean;
  requireImageApproval: boolean;
  seoReviewGate: boolean;
  legalReviewSensitive: boolean;
  publishingWindow: string;
  staggerInterval: string;
  maxDailyPosts: string;
  crossSiteSyndication: boolean;
  socialMediaAutoPost: boolean;
  rssFeedGeneration: boolean;
  newsletterAutoInclude: boolean;
  publishingFailureAlerts: boolean;
  sourceHealthAlerts: boolean;
  qualityDropAlerts: boolean;
  initialArticleCount: number;
};

const defaultData: WizardData = {
  siteName: "", siteSlug: "", siteDescription: "", siteNiche: "technology", siteTagline: "",
  primaryLang: "en", secondaryLangs: [], region: "Global", logoPreview: null, logoFile: null,
  editorialTone: "Neutral", legalEntity: "", publishingModel: "Automated + Review",
  theme: "modern-dark", layout: "magazine", templateId: null, colorPalette: ["#10B981","#1E293B","#F8FAFC","#6366F1","#F59E0B"],
  typography: "Inter + Merriweather", cardStyle: "Elevated Shadow", headerStyle: "Sticky Transparent", homepageStructure: "Hero + Grid",
  categories: ["ai","cybersecurity","startups"], articleTypes: ["News Articles","Analysis & Opinion"],
  publishingFrequency: "10-20 articles/day", evergreenRatio: 30, originalityThreshold: 85, rewriteIntensity: 75,
  internalLinking: true, autoSeoTags: true, humanReviewRequired: false,
  displayAds: true, affiliateLinks: true, sponsoredContent: false, newsletterMonetization: true,
  premiumSubscription: false, leadGeneration: false, adDensity: "Medium (4 ads/page)", affiliateNetwork: "Amazon Associates",
  ctaPlacements: ["After Intro","End of Article"],
  siteDomain: "", subdomain: "", hostingPlan: "standard", autoProvision: false, dbType: "MySQL 8.0",
  hostingProvider: "Delapresse-managed", cmsConnection: "NexusPress Internal", cdn: "Cloudflare",
  sslCertificate: "Auto (Let's Encrypt)", serverRegion: "Europe (Frankfurt)",
  autoDeployOnPublish: true, stagingEnvironment: true, emailSenderDomain: false,
  writingModel: "google/gemini-2.5-flash-lite", translationModel: "google/gemini-2.5-flash-lite", imageGeneration: "google/gemini-2.5-flash-lite",
  toneStyleRules: "", factConsistency: true, biasDetection: true, medicalLegalDisclaimer: false,
  contentSafetyFilter: true, bannedTopics: "", bannedClaims: "", requiredAttribution: "Always include source",
  titleGeneration: "SEO-optimized (60 chars)", metaDescriptionStyle: "Informative summary (155 chars)",
  pollingFrequency: "Every 15 minutes", maxArticlesPerPoll: "10 articles",
  autoApproveHighTrust: true, requireImageApproval: false, seoReviewGate: true, legalReviewSensitive: false,
  publishingWindow: "24/7 Continuous", staggerInterval: "15 min between posts", maxDailyPosts: "20",
  crossSiteSyndication: false, socialMediaAutoPost: true, rssFeedGeneration: true, newsletterAutoInclude: true,
  publishingFailureAlerts: true, sourceHealthAlerts: true, qualityDropAlerts: false,
  initialArticleCount: 10,
};

const templateTypeThemeMap: Record<string, WizardData["theme"]> = {
  broadsheet: "editorial",
  broadcast: "bold-accent",
  magazine: "clean-light",
  digital: "tech-minimal",
  niche: "warm-organic",
};

function normalizeSeedToken(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function mapTemplateToLayout(layout: string): WizardData["layout"] {
  const normalized = layout.toLowerCase();
  if (normalized.includes("sidebar")) return "sidebar";
  if (normalized.includes("card")) return "cards";
  if (normalized.includes("minimal")) return "minimal";
  if (normalized.includes("feed") || normalized.includes("longform")) return "blog";
  if (normalized.includes("portal") || normalized.includes("hub") || normalized.includes("broadsheet") || normalized.includes("broadcast")) return "portal";
  return "magazine";
}

function mapTemplateToTypography(typography: string): WizardData["typography"] {
  const normalized = typography.toLowerCase();
  if (normalized.includes("grotesk") || normalized.includes("egyptian") || normalized.includes("sans")) return "DM Sans + Lora";
  if (normalized.includes("bodoni") || normalized.includes("didot") || normalized.includes("display")) return "Outfit + Source Serif";
  if (normalized.includes("mono") || normalized.includes("helvetica") || normalized.includes("atlas")) return "Space Grotesk + Charter";
  if (normalized.includes("garamond") || normalized.includes("charter") || normalized.includes("serif")) return "Inter + Merriweather";
  return defaultData.typography;
}

function applyTemplatePreset(templateId: string | null): Partial<WizardData> {
  if (!templateId) return { templateId: null };
  const template = templateModels.find((item) => item.id === templateId);
  if (!template) return { templateId };

  return {
    templateId: template.id,
    theme: templateTypeThemeMap[template.type] || defaultData.theme,
    layout: mapTemplateToLayout(template.layout),
    typography: mapTemplateToTypography(template.typography),
    colorPalette: template.previewColors.slice(0, 5),
  };
}

function buildWebsiteConfigs(data: WizardData) {
  const activeTemplate = data.templateId ? templateModels.find((template) => template.id === data.templateId) : null;
  const languages = [data.primaryLang, ...data.secondaryLangs];

  const theme_config = {
    theme: data.theme,
    layout: data.layout,
    templateId: data.templateId,
    templateName: activeTemplate?.name || null,
    colorPalette: data.colorPalette,
    typography: data.typography,
    cardStyle: data.cardStyle,
    headerStyle: data.headerStyle,
    homepageStructure: data.homepageStructure,
    editorialTone: data.editorialTone,
  };

  const content_config = {
    categories: data.categories,
    articleTypes: data.articleTypes,
    publishingFrequency: data.publishingFrequency,
    evergreenRatio: data.evergreenRatio,
    originalityThreshold: data.originalityThreshold,
    rewriteIntensity: data.rewriteIntensity,
    internalLinking: data.internalLinking,
    autoSeoTags: data.autoSeoTags,
    humanReviewRequired: data.humanReviewRequired,
    publishingModel: data.publishingModel,
    legalEntity: data.legalEntity,
    initialArticleCount: data.initialArticleCount,
    // Editorial region (e.g. "Europe", "North America", "Global") — drives
    // localisation, content sourcing region filters, and site geo-routing.
    // Previously captured in step 1 but never persisted.
    region: data.region,
  };

  const monetization_config = {
    displayAds: data.displayAds,
    affiliateLinks: data.affiliateLinks,
    sponsoredContent: data.sponsoredContent,
    newsletterMonetization: data.newsletterMonetization,
    premiumSubscription: data.premiumSubscription,
    leadGeneration: data.leadGeneration,
    adDensity: data.adDensity,
    affiliateNetwork: data.affiliateNetwork,
    ctaPlacements: data.ctaPlacements,
  };

  const technical_config = {
    hostingPlan: data.hostingPlan,
    hostingProvider: data.hostingProvider,
    cmsConnection: data.cmsConnection,
    cdn: data.cdn,
    sslCertificate: data.sslCertificate,
    serverRegion: data.serverRegion,
    dbType: data.dbType,
    subdomain: data.subdomain,
    autoProvision: false,
    autoDeployOnPublish: data.autoDeployOnPublish,
    stagingEnvironment: data.stagingEnvironment,
    emailSenderDomain: data.emailSenderDomain,
  };

  const ai_config = {
    writingModel: data.writingModel,
    translationModel: data.translationModel,
    imageGeneration: data.imageGeneration,
    toneStyleRules: data.toneStyleRules,
    factConsistency: data.factConsistency,
    biasDetection: data.biasDetection,
    medicalLegalDisclaimer: data.medicalLegalDisclaimer,
    contentSafetyFilter: data.contentSafetyFilter,
    bannedTopics: data.bannedTopics,
    bannedClaims: data.bannedClaims,
    requiredAttribution: data.requiredAttribution,
    titleGeneration: data.titleGeneration,
    metaDescriptionStyle: data.metaDescriptionStyle,
  };

  const automation_config = {
    pollingFrequency: data.pollingFrequency,
    maxArticlesPerPoll: data.maxArticlesPerPoll,
    autoApproveHighTrust: data.autoApproveHighTrust,
    requireImageApproval: data.requireImageApproval,
    seoReviewGate: data.seoReviewGate,
    legalReviewSensitive: data.legalReviewSensitive,
    publishingWindow: data.publishingWindow,
    staggerInterval: data.staggerInterval,
    maxDailyPosts: data.maxDailyPosts,
    crossSiteSyndication: data.crossSiteSyndication,
    socialMediaAutoPost: data.socialMediaAutoPost,
    rssFeedGeneration: data.rssFeedGeneration,
    newsletterAutoInclude: data.newsletterAutoInclude,
    publishingFailureAlerts: data.publishingFailureAlerts,
    sourceHealthAlerts: data.sourceHealthAlerts,
    qualityDropAlerts: data.qualityDropAlerts,
  };

  const deployStrategy = data.autoDeployOnPublish ? "auto" : "manual";
  const syncFreq = data.pollingFrequency === "Every 5 minutes"
    ? "5m"
    : data.pollingFrequency === "Every 15 minutes"
      ? "15m"
      : data.pollingFrequency === "Every 30 minutes"
        ? "30m"
        : data.pollingFrequency === "Every hour"
          ? "1h"
          : "6h";

  return {
    languages,
    theme_config,
    content_config,
    monetization_config,
    technical_config,
    ai_config,
    automation_config,
    deployStrategy,
    syncFreq,
  };
}

type SeedCandidate = {
  id: string;
  title: string;
  tags: string[] | null;
  status: string;
  updated_at: string;
  published_at: string | null;
};

function scoreSeedCandidate(article: SeedCandidate, data: WizardData) {
  const articleTags = (article.tags || []).map(normalizeSeedToken).filter(Boolean);
  const titleTokens = normalizeSeedToken(article.title).split(" ").filter(Boolean);
  const searchTerms = Array.from(new Set([
    data.siteNiche,
    ...data.categories,
    ...data.articleTypes,
  ].flatMap((value) => normalizeSeedToken(value).split(" ")).filter((token) => token.length > 2)));

  let score = 0;
  for (const term of searchTerms) {
    if (articleTags.some((tag) => tag.includes(term) || term.includes(tag))) score += 4;
    if (titleTokens.some((token) => token.includes(term) || term.includes(token))) score += 2;
  }

  if (article.status === "published") score += 3;
  if (article.status === "approved") score += 2;
  if (article.published_at) score += 1;

  return score;
}

// ─── Step 1 ──────────────────────────────────────────────
function StepIdentity({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  const niches = [
    { id: "technology", label: "Technology", icon: Database },
    { id: "finance", label: "Finance", icon: CreditCard },
    { id: "politics", label: "Politics", icon: Building2 },
    { id: "health", label: "Health", icon: ThumbsUp },
    { id: "travel", label: "Travel", icon: MapPin },
    { id: "cybersecurity", label: "Cybersecurity", icon: ShieldIcon },
    { id: "sports", label: "Sports", icon: Rocket },
    { id: "entertainment", label: "Entertainment", icon: Mic },
    { id: "business", label: "Business", icon: Scale },
    { id: "education", label: "Education", icon: BookOpen },
  ];

  const suggestedLangs = useMemo(() => {
    return allLanguages.filter(l => l.regions.includes(d.region) && l.code !== d.primaryLang);
  }, [d.region, d.primaryLang]);

  const toggleSecondaryLang = (code: string) => {
    set({ secondaryLangs: d.secondaryLangs.includes(code) ? d.secondaryLangs.filter(c => c !== code) : [...d.secondaryLangs, code] });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      set({ logoPreview: URL.createObjectURL(file), logoFile: file });
    }
  };

  const handleRegionChange = (newRegion: string) => {
    set({ region: newRegion });
    const regionLangs = allLanguages.filter(l => l.regions.includes(newRegion) && l.code !== d.primaryLang).map(l => l.code);
    if (d.secondaryLangs.length === 0 && regionLangs.length > 0) {
      set({ secondaryLangs: regionLangs.slice(0, 3) });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Site Name">
          <TextInput placeholder="e.g. TechPulse EU" value={d.siteName} onChange={(e: any) => {
            const name = e.target.value;
            set({ siteName: name });
            const autoSlug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
            if (!d.siteSlug || d.siteSlug === d.siteName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")) {
              set({ siteSlug: autoSlug });
            }
          }} />
        </Field>
        <Field label="Internal Code">
          <TextInput placeholder="e.g. techpulse-eu" value={d.siteSlug} onChange={(e: any) => set({ siteSlug: e.target.value })} />
        </Field>
      </div>
      <Field label="Site Logo">
        <div className="flex items-center gap-4">
          {d.logoPreview ? (
            <div className="relative group">
              <img src={d.logoPreview} alt="Logo" className="h-16 w-16 rounded-lg object-contain border border-border bg-card p-1" />
              <button onClick={() => set({ logoPreview: null, logoFile: null })} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </label>
          )}
          <div>
            <p className="text-sm text-foreground font-medium">Upload your logo</p>
            <p className="text-xs text-muted-foreground">Theme colors will be auto-suggested based on your logo</p>
          </div>
        </div>
      </Field>
      <Field label="Tagline">
        <TextInput placeholder="e.g. European tech innovation" value={d.siteTagline} onChange={(e: any) => set({ siteTagline: e.target.value })} />
      </Field>
      <Field label="Description">
        <TextArea placeholder="Brief description of the site's purpose and audience..." rows={3} value={d.siteDescription} onChange={(v) => set({ siteDescription: v })} />
      </Field>
      <Field label="Niche">
        <ChipSelect options={niches} selected={[d.siteNiche]} onToggle={(id) => set({ siteNiche: id })} />
      </Field>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Field label="Primary Language">
          <select value={d.primaryLang} onChange={(e) => set({ primaryLang: e.target.value })} className="w-full appearance-none rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring">
            {allLanguages.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
          </select>
        </Field>
        <Field label="Country / Region">
          <select value={d.region} onChange={(e) => handleRegionChange(e.target.value)} className="w-full appearance-none rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring">
            {["Global","Europe","North America","Middle East","Asia-Pacific","Africa","Latin America"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Editorial Tone">
          <SelectInput options={["Neutral","Authoritative","Casual","Formal","Conversational","Academic"]} value={d.editorialTone} onChange={(v) => set({ editorialTone: v })} />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Legal Entity / Owner">
          <TextInput placeholder="e.g. NexusMedia Group Ltd." value={d.legalEntity} onChange={(e: any) => set({ legalEntity: e.target.value })} />
        </Field>
        <Field label="Publishing Model">
          <SelectInput options={["Automated + Review","Fully Automated","Manual Only","Hybrid"]} value={d.publishingModel} onChange={(v) => set({ publishingModel: v })} />
        </Field>
      </div>
      <Field label="Additional Languages">
        <div className="space-y-3">
          {suggestedLangs.length > 0 && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/70 mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3" />Suggested for {d.region}</p>
              <div className="flex flex-wrap gap-2">
                {suggestedLangs.map(lang => {
                  const active = d.secondaryLangs.includes(lang.code);
                  return (
                    <button key={lang.code} onClick={() => toggleSecondaryLang(lang.code)} className={cn("flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all border", active ? "bg-primary/15 border-primary/40 text-primary font-medium" : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30")}>
                      <span>{lang.flag}</span><span>{lang.name}</span>{active && <Check className="h-3 w-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2">All Languages</p>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1">
              {allLanguages.filter(l => l.code !== d.primaryLang).map(lang => {
                const active = d.secondaryLangs.includes(lang.code);
                return (
                  <button key={lang.code} onClick={() => toggleSecondaryLang(lang.code)} className={cn("flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs transition-all border", active ? "bg-primary/15 border-primary/40 text-primary font-medium" : "bg-card border-border text-muted-foreground hover:text-foreground")}>
                    <span>{lang.flag}</span><span>{lang.name}</span>{active && <Check className="h-3 w-3" />}
                  </button>
                );
              })}
            </div>
          </div>
          {d.secondaryLangs.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Languages className="h-3.5 w-3.5 text-primary" />
              <span><span className="text-primary font-semibold">{d.secondaryLangs.length + 1}</span> languages selected — content will be auto-generated in all languages on publish</span>
            </div>
          )}
        </div>
      </Field>
    </div>
  );
}

// ─── Step 2: Design ──────────────────────────────────────
function StepDesign({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  const [searchParams] = useSearchParams();
  const urlTemplateId = searchParams.get("template");
  const effectiveTemplateId = d.templateId || urlTemplateId;
  const activeTemplate = effectiveTemplateId ? templateModels.find(t => t.id === effectiveTemplateId) : null;
  const navigate = useNavigate();

  // Sync URL template param into wizard state on mount. Intentionally
  // narrow deps: we ONLY want this to run when the URL param changes,
  // not when `set` or `d.templateId` change (which would re-apply the
  // preset and wipe user edits). The exhaustive-deps lint exception is
  // justified per React docs "Reading the latest props or state from
  // an Effect" pattern.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (urlTemplateId && !d.templateId) {
      const tpl = templateModels.find(t => t.id === urlTemplateId);
      if (tpl) {
        set(applyTemplatePreset(tpl.id));
      }
    }
    // Effect intentionally runs only when the URL template param changes.
    // Adding `set` / `d.templateId` would force a re-run after the preset
    // is applied and wipe later user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTemplateId]);

  const [showThemeSuggestion, setShowThemeSuggestion] = useState(!!d.logoPreview);
  const [themeConfirmed, setThemeConfirmed] = useState(false);

  const suggestedTemplates = useMemo(() => {
    if (!d.logoPreview) return [];
    return templateModels.slice(0, 3);
  }, [d.logoPreview]);

  const themes = [
    { id: "modern-dark", label: "Modern Dark", icon: Layers, desc: "Sleek, dark UI with high contrast" },
    { id: "clean-light", label: "Clean Light", icon: AlignLeft, desc: "Minimalist, bright and airy" },
    { id: "editorial", label: "Editorial", icon: BookOpen, desc: "Classic newspaper-inspired" },
    { id: "bold-accent", label: "Bold Accent", icon: Palette, desc: "Strong brand colors, modern" },
    { id: "tech-minimal", label: "Tech Minimal", icon: Grid3X3, desc: "Developer-focused, mono tones" },
    { id: "warm-organic", label: "Warm Organic", icon: MapPin, desc: "Earthy tones, natural feel" },
  ];

  const layouts = [
    { id: "magazine", label: "Magazine Grid", icon: LayoutGrid, desc: "Multi-column news grid" },
    { id: "blog", label: "Blog Feed", icon: AlignLeft, desc: "Single-column article feed" },
    { id: "portal", label: "News Portal", icon: Columns3, desc: "Dense, multi-section homepage" },
    { id: "sidebar", label: "Sidebar Layout", icon: PanelLeft, desc: "Content + sidebar widgets" },
    { id: "cards", label: "Card Grid", icon: Grid3X3, desc: "Visual card-based layout" },
    { id: "minimal", label: "Minimal Focus", icon: Layers, desc: "Single article focus" },
  ];

  return (
    <div className="space-y-6">
      {d.logoPreview && showThemeSuggestion && !themeConfirmed && (
        <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15"><Sparkles className="h-5 w-5 text-primary" /></div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Auto-Theme Suggestion</h3>
                <p className="text-xs text-muted-foreground">Based on your logo, we extracted colors and suggest matching templates</p>
              </div>
            </div>
            <button onClick={() => setShowThemeSuggestion(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2">Extracted Palette</p>
            <div className="flex items-center gap-3">
              <img src={d.logoPreview} alt="Logo" className="h-12 w-12 rounded-lg object-contain border border-border bg-card p-1" />
              <div className="flex h-px flex-1 bg-gradient-to-r from-border via-primary/30 to-border" />
              <div className="flex items-center gap-2">
                {d.colorPalette.map((c, i) => (
                  <div key={i} className="group relative">
                    <div className="h-10 w-10 rounded-lg border-2 border-border cursor-pointer hover:scale-110 transition-transform shadow-sm" style={{ backgroundColor: c }} />
                    <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {suggestedTemplates.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2">Suggested Templates</p>
              <div className="grid grid-cols-3 gap-3">
                {suggestedTemplates.map(t => (
                  <button key={t.id} onClick={() => set({ theme: t.id, templateId: t.id })} className={cn("rounded-lg border p-3 text-left transition-all", d.theme === t.id ? "border-primary/40 bg-primary/10 shadow-sm" : "border-border bg-card hover:border-primary/20")}>
                    <div className="flex items-center gap-2 mb-1"><div className="flex gap-1">{t.previewColors.slice(0, 3).map((c, i) => <div key={i} className="h-3 w-3 rounded-full" style={{ backgroundColor: c }} />)}</div></div>
                    <p className="text-xs font-medium text-foreground">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground">{t.tagline}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => { setThemeConfirmed(true); setShowThemeSuggestion(false); }} className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"><Check className="h-4 w-4" />Confirm Theme & Colors</button>
        </div>
      )}
      {themeConfirmed && (
        <div className="rounded-lg border border-success/30 bg-success/5 p-3 flex items-center justify-between">
          <div className="flex items-center gap-3"><Check className="h-5 w-5 text-success" /><div><p className="text-sm font-medium text-foreground">Theme confirmed from logo</p><p className="text-xs text-muted-foreground">Colors and template applied — you can still customize below</p></div></div>
          <button onClick={() => { setThemeConfirmed(false); setShowThemeSuggestion(true); }} className="text-xs text-primary hover:underline">Re-suggest</button>
        </div>
      )}
      {activeTemplate ? (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <activeTemplate.icon className="h-5 w-5 text-primary" />
            <div><p className="text-sm font-medium text-foreground">Template: <span className="text-primary">{activeTemplate.name}</span></p><p className="text-xs text-muted-foreground">{activeTemplate.tagline} — {activeTemplate.layout} • {activeTemplate.typography}</p></div>
          </div>
          <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /><span className="text-xs text-primary font-medium">Applied</span></div>
        </div>
      ) : (
        <button onClick={() => navigate("/templates")} className="w-full rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4 flex items-center justify-center gap-2 text-sm text-primary hover:bg-primary/10 transition-colors"><Rocket className="h-4 w-4" />Start from a Template — Browse News Portal, Magazine & Tech Blog presets<ChevronRight className="h-4 w-4" /></button>
      )}
      <Field label="Template Preset">
        <div className="space-y-2">
          <select
            value={effectiveTemplateId ?? ""}
            onChange={(e) => set(applyTemplatePreset(e.target.value || null))}
            className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Select a template preset…</option>
            {templateModels.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} — {template.tagline}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-muted-foreground">This preset controls the theme package plus the starter layout, typography, and palette used during provisioning.</p>
        </div>
      </Field>
      <Field label="Theme"><CardSelect options={themes} selected={d.theme} onSelect={(id) => set({ theme: id })} /></Field>
      <Field label="Layout Family"><CardSelect options={layouts} selected={d.layout} onSelect={(id) => set({ layout: id })} /></Field>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Color Palette">
          <div className="flex items-center gap-3 flex-wrap">
            {d.colorPalette.map((c, i) => (
              <div key={i} className="relative group">
                <label className="block h-8 w-8 rounded-full border border-border cursor-pointer hover:scale-110 transition-transform shadow-sm" style={{ backgroundColor: c }}>
                  <input
                    type="color"
                    value={c}
                    onChange={(e) => {
                      const updated = [...d.colorPalette];
                      updated[i] = e.target.value;
                      set({ colorPalette: updated });
                    }}
                    className="sr-only"
                  />
                </label>
                {d.colorPalette.length > 2 && (
                  <button
                    onClick={() => set({ colorPalette: d.colorPalette.filter((_, j) => j !== i) })}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            ))}
            {d.colorPalette.length < 8 && (
              <label className="h-8 w-8 rounded-full border border-dashed border-muted-foreground/40 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                <span className="text-lg leading-none">+</span>
                <input
                  type="color"
                  defaultValue="#888888"
                  onChange={(e) => set({ colorPalette: [...d.colorPalette, e.target.value] })}
                  className="sr-only"
                />
              </label>
            )}
          </div>
        </Field>
        <Field label="Typography"><SelectInput options={["Inter + Merriweather","DM Sans + Lora","Space Grotesk + Charter","Outfit + Source Serif","Geist + Georgia"]} value={d.typography} onChange={(v) => set({ typography: v })} /></Field>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Field label="Card Style"><SelectInput options={["Elevated Shadow","Bordered","Flat","Glassmorphism","Outlined"]} value={d.cardStyle} onChange={(v) => set({ cardStyle: v })} /></Field>
        <Field label="Header Style"><SelectInput options={["Sticky Transparent","Solid Dark","Minimal","Mega Menu","Centered Logo"]} value={d.headerStyle} onChange={(v) => set({ headerStyle: v })} /></Field>
        <Field label="Homepage Structure"><SelectInput options={["Hero + Grid","Breaking Banner + Feed","Category Sections","Featured + Sidebar","Full Grid"]} value={d.homepageStructure} onChange={(v) => set({ homepageStructure: v })} /></Field>
      </div>
    </div>
  );
}

// ─── Step 3: Content Strategy ────────────────────────────
function StepContent({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  const categories = [
    { id: "ai", label: "AI & Machine Learning" },
    { id: "cybersecurity", label: "Cybersecurity" },
    { id: "startups", label: "Startups" },
    { id: "cloud", label: "Cloud Computing" },
    { id: "fintech", label: "FinTech" },
    { id: "gadgets", label: "Gadgets & Hardware" },
    { id: "policy", label: "Tech Policy" },
    { id: "reviews", label: "Reviews" },
  ];
  const allArticleTypes = ["News Articles","Analysis & Opinion","Tutorials","Listicles","Reviews","Interviews"];
  const toggleArticleType = (t: string) => set({ articleTypes: d.articleTypes.includes(t) ? d.articleTypes.filter(x => x !== t) : [...d.articleTypes, t] });

  return (
    <div className="space-y-6">
      <Field label="Categories">
        <ChipSelect options={categories} selected={d.categories} onToggle={(id) => set({ categories: d.categories.includes(id) ? d.categories.filter(c => c !== id) : [...d.categories, id] })} multi />
      </Field>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Field label="Article Types">
          <div className="space-y-2">
            {allArticleTypes.map(t => (
              <label key={t} className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                <input type="checkbox" checked={d.articleTypes.includes(t)} onChange={() => toggleArticleType(t)} className="accent-primary rounded" />{t}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Publishing Frequency">
          <div className="space-y-3">
            <SelectInput options={["10-20 articles/day","5-10 articles/day","1-5 articles/day","Weekly batches","Custom schedule"]} value={d.publishingFrequency} onChange={(v) => set({ publishingFrequency: v })} />
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Evergreen vs News ratio</label>
              <input type="range" min={0} max={100} value={d.evergreenRatio} onChange={(e) => set({ evergreenRatio: Number(e.target.value) })} className="w-full accent-primary h-1" />
              <div className="flex justify-between text-[10px] text-muted-foreground"><span>100% News</span><span>100% Evergreen</span></div>
            </div>
          </div>
        </Field>
        <Field label="Quality Controls">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Originality Threshold</label>
              <input type="range" min={50} max={100} value={d.originalityThreshold} onChange={(e) => set({ originalityThreshold: Number(e.target.value) })} className="w-full accent-primary h-1" />
              <div className="flex justify-between text-[10px] text-muted-foreground"><span>50%</span><span>100%</span></div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Rewrite Intensity</label>
              <input type="range" min={10} max={100} value={d.rewriteIntensity} onChange={(e) => set({ rewriteIntensity: Number(e.target.value) })} className="w-full accent-primary h-1" />
              <div className="flex justify-between text-[10px] text-muted-foreground"><span>Light</span><span>Full</span></div>
            </div>
          </div>
        </Field>
      </div>
      <div className="space-y-2">
        <ToggleRow label="Internal Linking" desc="Automatically link to related articles across the site" on={d.internalLinking} onToggle={(v) => set({ internalLinking: v })} />
        <ToggleRow label="Auto SEO Tags" desc="Generate meta titles, descriptions, and tags automatically" on={d.autoSeoTags} onToggle={(v) => set({ autoSeoTags: v })} />
        <ToggleRow label="Human Review Required" desc="Require editor approval before publishing" on={d.humanReviewRequired} onToggle={(v) => set({ humanReviewRequired: v })} />
      </div>
      <Field label="Initial Content Seeding">
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10"><Sparkles className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-sm font-medium text-foreground">Auto-link existing articles</p>
              <p className="text-xs text-muted-foreground">Matching articles by niche, categories & language will be linked to this site on creation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-muted-foreground whitespace-nowrap">Number of articles:</label>
            <input
              type="number"
              min={0}
              max={100}
              value={d.initialArticleCount}
              onChange={(e) => set({ initialArticleCount: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
              className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
            />
            <span className="text-[10px] text-muted-foreground">0 = disabled</span>
          </div>
        </div>
      </Field>
    </div>
  );
}

// ─── Step 4: Monetization ────────────────────────────────
function StepMonetization({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  const allPlacements = ["After Intro","Mid-Article","End of Article","Sidebar","Sticky Footer","Exit Intent Popup"];
  const toggleCta = (p: string) => set({ ctaPlacements: d.ctaPlacements.includes(p) ? d.ctaPlacements.filter(x => x !== p) : [...d.ctaPlacements, p] });

  return (
    <div className="space-y-6">
      <Field label="Revenue Channels">
        <div className="space-y-2">
          <ToggleRow label="Display Ads" desc="Google AdSense, programmatic ad placements" on={d.displayAds} onToggle={(v) => set({ displayAds: v })} />
          <ToggleRow label="Affiliate Links" desc="Product links with revenue tracking" on={d.affiliateLinks} onToggle={(v) => set({ affiliateLinks: v })} />
          <ToggleRow label="Sponsored Content" desc="Dedicated sponsor zones and branded articles" on={d.sponsoredContent} onToggle={(v) => set({ sponsoredContent: v })} />
          <ToggleRow label="Newsletter Monetization" desc="Newsletter signup forms with premium sponsorships" on={d.newsletterMonetization} onToggle={(v) => set({ newsletterMonetization: v })} />
          <ToggleRow label="Premium / Subscription" desc="Paywall for exclusive content" on={d.premiumSubscription} onToggle={(v) => set({ premiumSubscription: v })} />
          <ToggleRow label="Lead Generation" desc="Contact forms and gated content for leads" on={d.leadGeneration} onToggle={(v) => set({ leadGeneration: v })} />
        </div>
      </Field>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Ad Placement Density"><SelectInput options={["Low (2 ads/page)","Medium (4 ads/page)","High (6+ ads/page)","Custom"]} value={d.adDensity} onChange={(v) => set({ adDensity: v })} /></Field>
        <Field label="Affiliate Network"><SelectInput options={["Amazon Associates","ShareASale","CJ Affiliate","Custom / Multiple","None yet"]} value={d.affiliateNetwork} onChange={(v) => set({ affiliateNetwork: v })} /></Field>
      </div>
      <Field label="CTA Widget Placement">
        <div className="flex flex-wrap gap-2">
          {allPlacements.map(p => (
            <label key={p} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
              <input type="checkbox" checked={d.ctaPlacements.includes(p)} onChange={() => toggleCta(p)} className="accent-primary rounded" />{p}
            </label>
          ))}
        </div>
      </Field>
    </div>
  );
}

// ─── Step 5: Technical ───────────────────────────────────
function StepTechnical({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10"><Cloud className="h-5 w-5 text-primary" /></div>
          <div><p className="text-sm font-medium text-foreground">Delapresse website</p><p className="text-xs text-muted-foreground">The website is created in Delapresse only. External hosting credentials can be added later from the website details page.</p></div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Domain"><TextInput placeholder="e.g. techpulse.eu" value={d.siteDomain} onChange={(e: any) => set({ siteDomain: e.target.value })} /><p className="text-[10px] text-muted-foreground mt-1">Used as the Delapresse site domain and optional external destination domain</p></Field>
        <Field label="Subdomain (optional)"><TextInput placeholder="e.g. blog.techpulse.eu" value={d.subdomain} onChange={(e: any) => set({ subdomain: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Field label="Hosting Provider"><SelectInput options={["Delapresse-managed"]} value="Delapresse-managed" onChange={() => {}} /></Field>
        <Field label="CMS Connection"><SelectInput options={["NexusPress Internal","Managed REST API","Headless CMS","Static Site","Custom API"]} value={d.cmsConnection} onChange={(v) => set({ cmsConnection: v })} /></Field>
        <Field label="CDN"><SelectInput options={["Cloudflare","AWS CloudFront","Fastly","Built-in CDN","None"]} value={d.cdn} onChange={(v) => set({ cdn: v })} /></Field>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="SSL Certificate"><SelectInput options={["Auto (Let's Encrypt)","Custom SSL","Cloudflare SSL"]} value={d.sslCertificate} onChange={(v) => set({ sslCertificate: v })} /></Field>
        <Field label="Server Region"><SelectInput options={["Europe (Frankfurt)","US East (Virginia)","US West (Oregon)","Asia (Singapore)","Middle East (Bahrain)"]} value={d.serverRegion} onChange={(v) => set({ serverRegion: v })} /></Field>
      </div>
      <Field label="Additional Settings">
        <div className="space-y-2">
          <ToggleRow label="Auto-deploy on Publish" desc="Push content changes to production automatically" on={d.autoDeployOnPublish} onToggle={(v) => set({ autoDeployOnPublish: v })} />
          <ToggleRow label="Staging Environment" desc="Preview changes before going live" on={d.stagingEnvironment} onToggle={(v) => set({ stagingEnvironment: v })} />
          <ToggleRow label="Email Sender Domain" desc="Configure SPF/DKIM for transactional emails" on={d.emailSenderDomain} onToggle={(v) => set({ emailSenderDomain: v })} />
        </div>
      </Field>
    </div>
  );
}

// ─── Step 6: AI Rules ────────────────────────────────────
function StepAI({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Field label="Writing Model"><SelectInput options={["google/gemini-2.5-flash-lite"]} value={d.writingModel} onChange={(v) => set({ writingModel: v })} /></Field>
        <Field label="Translation Model"><SelectInput options={["google/gemini-2.5-flash-lite"]} value={d.translationModel} onChange={(v) => set({ translationModel: v })} /></Field>
        <Field label="Image Generation"><SelectInput options={["google/gemini-2.5-flash-lite"]} value={d.imageGeneration} onChange={(v) => set({ imageGeneration: v })} /></Field>
      </div>
      <Field label="Tone & Style Rules">
        <TextArea placeholder="e.g. Always write in third person. Use active voice. Keep sentences under 25 words. Avoid jargon unless explaining technical concepts..." rows={4} value={d.toneStyleRules} onChange={(v) => set({ toneStyleRules: v })} />
      </Field>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Safety & Compliance">
          <div className="space-y-2">
            <ToggleRow label="Fact Consistency Mode" desc="Cross-check facts against source material" on={d.factConsistency} onToggle={(v) => set({ factConsistency: v })} />
            <ToggleRow label="Bias Detection" desc="Flag potentially biased language" on={d.biasDetection} onToggle={(v) => set({ biasDetection: v })} />
            <ToggleRow label="Medical/Legal Disclaimer" desc="Auto-insert disclaimers for sensitive topics" on={d.medicalLegalDisclaimer} onToggle={(v) => set({ medicalLegalDisclaimer: v })} />
            <ToggleRow label="Content Safety Filter" desc="Block harmful, violent, or inappropriate content" on={d.contentSafetyFilter} onToggle={(v) => set({ contentSafetyFilter: v })} />
          </div>
        </Field>
        <Field label="Content Guardrails">
          <div className="space-y-4">
            <Field label="Banned Topics"><TextInput placeholder="e.g. gambling, adult content, hate speech..." value={d.bannedTopics} onChange={(e: any) => set({ bannedTopics: e.target.value })} /></Field>
            <Field label="Banned Claims"><TextInput placeholder="e.g. guaranteed returns, miracle cures..." value={d.bannedClaims} onChange={(e: any) => set({ bannedClaims: e.target.value })} /></Field>
            <Field label="Required Attribution"><SelectInput options={["Always include source","Source in footer only","No attribution required"]} value={d.requiredAttribution} onChange={(v) => set({ requiredAttribution: v })} /></Field>
          </div>
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Field label="Title Generation"><SelectInput options={["SEO-optimized (60 chars)","Clickbait-resistant factual","Question-based","Number-led listicle","Breaking news style"]} value={d.titleGeneration} onChange={(v) => set({ titleGeneration: v })} /></Field>
        <Field label="Meta Description Style"><SelectInput options={["Informative summary (155 chars)","CTA-driven","Question + answer","Keyword-rich"]} value={d.metaDescriptionStyle} onChange={(v) => set({ metaDescriptionStyle: v })} /></Field>
      </div>
    </div>
  );
}

// ─── Step 7: Automation ──────────────────────────────────
function StepAutomation({ d, set }: { d: WizardData; set: (p: Partial<WizardData>) => void }) {
  return (
    <div className="space-y-6">
      <Field label="Source Polling">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Field label="Polling Frequency"><SelectInput options={["Every 5 minutes","Every 15 minutes","Every 30 minutes","Every hour","Every 6 hours"]} value={d.pollingFrequency} onChange={(v) => set({ pollingFrequency: v })} /></Field>
          <Field label="Max Articles per Poll"><SelectInput options={["5 articles","10 articles","25 articles","50 articles","Unlimited"]} value={d.maxArticlesPerPoll} onChange={(v) => set({ maxArticlesPerPoll: v })} /></Field>
        </div>
      </Field>
      <Field label="Approval Flow">
        <div className="space-y-2">
          <ToggleRow label="Auto-approve High-Trust Sources" desc="Skip review for sources with 90%+ trust score" on={d.autoApproveHighTrust} onToggle={(v) => set({ autoApproveHighTrust: v })} />
          <ToggleRow label="Require Image Approval" desc="Hold articles until featured image is confirmed" on={d.requireImageApproval} onToggle={(v) => set({ requireImageApproval: v })} />
          <ToggleRow label="SEO Review Gate" desc="Require SEO score above threshold before publishing" on={d.seoReviewGate} onToggle={(v) => set({ seoReviewGate: v })} />
          <ToggleRow label="Legal Review for Sensitive Topics" desc="Route flagged content to legal team" on={d.legalReviewSensitive} onToggle={(v) => set({ legalReviewSensitive: v })} />
        </div>
      </Field>
      <Field label="Publishing Schedule">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Field label="Publishing Window"><SelectInput options={["24/7 Continuous","Business Hours (9-18)","Peak Traffic Hours","Morning + Evening","Custom Schedule"]} value={d.publishingWindow} onChange={(v) => set({ publishingWindow: v })} /></Field>
          <Field label="Stagger Interval"><SelectInput options={["No delay","5 min between posts","15 min between posts","30 min between posts","1 hour between posts"]} value={d.staggerInterval} onChange={(v) => set({ staggerInterval: v })} /></Field>
          <Field label="Max Daily Posts"><SelectInput options={["5","10","20","50","100","Unlimited"]} value={d.maxDailyPosts} onChange={(v) => set({ maxDailyPosts: v })} /></Field>
        </div>
      </Field>
      <Field label="Distribution & Syndication">
        <div className="space-y-2">
          <ToggleRow label="Cross-site Syndication" desc="Distribute articles to other sites in your network" on={d.crossSiteSyndication} onToggle={(v) => set({ crossSiteSyndication: v })} />
          <ToggleRow label="Social Media Auto-post" desc="Push to connected social accounts on publish" on={d.socialMediaAutoPost} onToggle={(v) => set({ socialMediaAutoPost: v })} />
          <ToggleRow label="RSS Feed Generation" desc="Auto-generate RSS feed for the site" on={d.rssFeedGeneration} onToggle={(v) => set({ rssFeedGeneration: v })} />
          <ToggleRow label="Newsletter Auto-include" desc="Include new articles in weekly newsletter digest" on={d.newsletterAutoInclude} onToggle={(v) => set({ newsletterAutoInclude: v })} />
        </div>
      </Field>
      <Field label="Alerts & Monitoring">
        <div className="space-y-2">
          <ToggleRow label="Publishing Failure Alerts" desc="Notify on failed publishing jobs" on={d.publishingFailureAlerts} onToggle={(v) => set({ publishingFailureAlerts: v })} />
          <ToggleRow label="Source Health Alerts" desc="Notify when source feeds go down" on={d.sourceHealthAlerts} onToggle={(v) => set({ sourceHealthAlerts: v })} />
          <ToggleRow label="Quality Drop Alerts" desc="Notify when originality or SEO scores drop" on={d.qualityDropAlerts} onToggle={(v) => set({ qualityDropAlerts: v })} />
        </div>
      </Field>
    </div>
  );
}

// ─── Step 8: Review & Launch ─────────────────────────────
function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-foreground text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function StepReview({ d }: { d: WizardData }) {
  const activeTemplate = d.templateId ? templateModels.find(t => t.id === d.templateId) : null;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
        <div>
          <p className="text-sm font-medium text-foreground">Ready to launch</p>
          <p className="text-xs text-muted-foreground">Review your configuration below. Click "Launch Website" to create your site.</p>
        </div>
      </div>

      {/* Identity */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />Site Identity</h4>
        <SummaryRow label="Name" value={d.siteName || "—"} />
        <SummaryRow label="Slug" value={d.siteSlug || "—"} />
        <SummaryRow label="Niche" value={d.siteNiche || "—"} />
        <SummaryRow label="Tagline" value={d.siteTagline || "—"} />
        <SummaryRow label="Primary Language" value={allLanguages.find(l => l.code === d.primaryLang)?.name || d.primaryLang} />
        <SummaryRow label="Additional Languages" value={d.secondaryLangs.length > 0 ? d.secondaryLangs.map(c => allLanguages.find(l => l.code === c)?.name || c).join(", ") : "None"} />
        <SummaryRow label="Region" value={d.region} />
        <SummaryRow label="Tone" value={d.editorialTone} />
      </div>

      {/* Design */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" />Design</h4>
        <SummaryRow label="Template" value={activeTemplate?.name || "None selected"} />
        <SummaryRow label="Theme" value={d.theme} />
        <SummaryRow label="Layout" value={d.layout} />
        <SummaryRow label="Typography" value={d.typography} />
        <SummaryRow label="Colors" value={
          <div className="flex gap-1">{d.colorPalette.map((c, i) => <div key={i} className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: c }} />)}</div>
        } />
      </div>

      {/* Content */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Content Strategy</h4>
        <SummaryRow label="Categories" value={d.categories.join(", ")} />
        <SummaryRow label="Article Types" value={d.articleTypes.join(", ")} />
        <SummaryRow label="Frequency" value={d.publishingFrequency} />
        <SummaryRow label="Initial Articles" value={d.initialArticleCount > 0 ? `${d.initialArticleCount} articles (auto-linked)` : "Disabled"} />
        <SummaryRow label="Human Review" value={d.humanReviewRequired ? "Required" : "Not required"} />
      </div>

      {/* Technical */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5"><Server className="h-3.5 w-3.5" />Technical</h4>
        <SummaryRow label="Domain" value={d.siteDomain || "Not set"} />
        <SummaryRow label="Storage" value="Delapresse database only" />
        <SummaryRow label="External Hosting" value="Optional after creation" />
        <SummaryRow label="CDN" value={d.cdn} />
        <SummaryRow label="Publish/Sync" value="Manual from website deployment settings" />
      </div>

      {/* AI */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />AI Configuration</h4>
        <SummaryRow label="Writing Model" value={d.writingModel} />
        <SummaryRow label="Translation" value={d.translationModel} />
        <SummaryRow label="Image Gen" value={d.imageGeneration} />
        <SummaryRow label="Safety Filter" value={d.contentSafetyFilter ? "Enabled" : "Disabled"} />
      </div>

      {/* Automation */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <h4 className="text-[10px] font-semibold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" />Automation</h4>
        <SummaryRow label="Polling" value={d.pollingFrequency} />
        <SummaryRow label="Max Articles/Poll" value={d.maxArticlesPerPoll} />
        <SummaryRow label="Max Daily Posts" value={d.maxDailyPosts} />
        <SummaryRow label="Publishing Window" value={d.publishingWindow} />
        <SummaryRow label="Social Auto-Post" value={d.socialMediaAutoPost ? "Enabled" : "Disabled"} />
      </div>
    </div>
  );
}

function SyncStatusBanner({ status, error, onRetry }: { status: "idle" | "saving" | "provisioning" | "done" | "error"; error?: string; onRetry?: () => void }) {
  if (status === "idle") return null;
  const configs = {
    saving: { icon: Loader2, label: "Saving website configuration...", cls: "border-primary/30 bg-primary/5 text-primary", spin: true },
    provisioning: { icon: Loader2, label: "Running requested hosting action...", cls: "border-primary/30 bg-primary/5 text-primary", spin: true },
    done: { icon: CheckCircle2, label: "Website created successfully.", cls: "border-success/30 bg-success/5 text-success", spin: false },
    error: { icon: AlertCircle, label: error || "An error occurred", cls: "border-destructive/30 bg-destructive/5 text-destructive", spin: false },
  };
  const c = configs[status];
  return (
    <div className={cn("fixed bottom-20 left-1/2 -translate-x-1/2 z-50 rounded-xl border px-5 py-3 shadow-lg flex items-center gap-3 max-w-lg", c.cls)}>
      <c.icon className={cn("h-5 w-5 shrink-0", c.spin && "animate-spin")} />
      <span className="text-sm font-medium">{c.label}</span>
      {status === "error" && onRetry && (
        <button onClick={onRetry} className="ml-2 rounded-md bg-destructive/10 px-3 py-1 text-xs font-medium hover:bg-destructive/20 transition-colors flex items-center gap-1">
          <RefreshCw className="h-3 w-3" />Retry
        </button>
      )}
    </div>
  );
}

// ─── Validation ──────────────────────────────────────────
function validateAllSteps(d: WizardData): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!d.siteName.trim()) errors.push("Site Name is required (Step 1)");
  if (!d.siteSlug.trim()) errors.push("Internal Code is required (Step 1)");
  if (!d.siteNiche) errors.push("Niche selection is required (Step 1)");
  if (!d.templateId) errors.push("Template selection is required (Step 2)");
  if (d.categories.length === 0) errors.push("At least one category is required (Step 3)");
  if (d.articleTypes.length === 0) errors.push("At least one article type is required (Step 3)");
  return { valid: errors.length === 0, errors };
}

// ─── Main Wizard ─────────────────────────────────────────
export default function CreateWebsiteWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [syncStatus, setSyncStatus] = useState<"idle" | "saving" | "provisioning" | "done" | "error">("idle");
  const [syncError, setSyncError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentCompany, currentCompanyId, isPlatformOwner } = useCompany();
  const trialMode = useTrialMode();
  const urlTemplateId = searchParams.get("template");
  const [data, setData] = useState<WizardData>(() => ({
    ...defaultData,
    ...applyTemplatePreset(urlTemplateId),
  }));
  const createdWebsiteRef = useRef<string | null>(null);

  const set = useCallback((partial: Partial<WizardData>) => {
    setData(prev => ({ ...prev, ...partial }));
  }, []);

  useEffect(() => {
    if (!urlTemplateId) return;
    const selectedTemplate = templateModels.find((template) => template.id === urlTemplateId);
    if (!selectedTemplate) return;

    setData((prev) => (
      prev.templateId === selectedTemplate.id
        ? prev
        : { ...prev, ...applyTemplatePreset(selectedTemplate.id) }
    ));
  }, [urlTemplateId]);

  const handleLaunch = async () => {
    if (trialMode.isReadOnly) {
      toast.error(TRIAL_READ_ONLY_MESSAGE);
      return;
    }
    if (!user) { toast.error("You must be logged in to create a website"); return; }
    if (!currentCompanyId) { toast.error("Select a company before creating a website"); return; }

    const { valid, errors } = validateAllSteps(data);
    if (!valid) {
      errors.forEach(e => toast.error(e));
      return;
    }

    setSyncStatus("saving");
    setSyncError("");

    if (!isPlatformOwner && currentCompany?.subscription_plan_id) {
      const [{ count }, { data: plan }] = await Promise.all([
        (supabase as any).from("websites").select("id", { count: "exact", head: true }).eq("company_id", currentCompanyId),
        (supabase as any).from("subscription_plans").select("website_limit").eq("id", currentCompany.subscription_plan_id).maybeSingle(),
      ]);
      const limit = plan?.website_limit ?? 1;
      if (limit >= 0 && (count ?? 0) >= limit) {
        setSyncStatus("error");
        setSyncError(`Your company plan allows ${limit} website${limit === 1 ? "" : "s"}.`);
        toast.error("Website limit reached");
        return;
      }
    }

    const {
      languages,
      theme_config,
      content_config,
      monetization_config,
      technical_config,
      ai_config,
      automation_config,
      deployStrategy,
      syncFreq,
    } = buildWebsiteConfigs(data);

    // Upload logo to storage if file exists
    let logoUrl = data.logoPreview;
    if (data.logoFile) {
      const ext = data.logoFile.name.split(".").pop();
      const path = `logos/${data.siteSlug}-${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("media").upload(path, data.logoFile);
      if (!uploadErr) {
        const { data: urlData } = supabase.storage.from("media").getPublicUrl(path);
        logoUrl = urlData.publicUrl;
      }
    }

    const { data: insertedWebsite, error } = await supabase.from("websites").insert({
      owner_id: user.id,
      company_id: currentCompanyId,
      name: data.siteName.trim(),
      slug: data.siteSlug.trim(),
      domain: data.siteDomain.trim() || null,
      tagline: data.siteTagline.trim() || null,
      description: data.siteDescription.trim() || null,
      niche: data.siteNiche.charAt(0).toUpperCase() + data.siteNiche.slice(1),
      languages,
      primary_language: data.primaryLang,
      logo_url: logoUrl,
      template_id: data.templateId,
      status: "active",
      deployment_strategy: deployStrategy,
      sync_frequency: syncFreq,
      external_hosting_provider: null,
      external_connection_required: false,
      sync_enabled: false,
      sync_configuration: {},
      publish_status: "not_published",
      sync_status: "not_synced",
      theme_config: theme_config as any,
      content_config: content_config as any,
      monetization_config: monetization_config as any,
      technical_config: technical_config as any,
      ai_config: ai_config as any,
      automation_config: automation_config as any,
    } as any).select().single();

    if (error) {
      setSyncStatus("error");
      // The enforce_max_websites DB trigger raises a structured error tagged
      // 'WEBSITES_OVER_LIMIT'. Translate it into a clear upgrade prompt and
      // record an expansion_events signal so the daily monitor surfaces it.
      if (typeof error.message === "string" && error.message.includes("WEBSITES_OVER_LIMIT")) {
        setSyncError("You've hit your plan's website limit. Upgrade to add more sites.");
        if (currentCompanyId) {
          const week = new Date().toISOString().slice(0, 10);
          // deno-lint-ignore no-explicit-any
          const dbx = supabase as any;
          await dbx.from("expansion_events").upsert({
            company_id: currentCompanyId,
            event_type: "starter_second_site",
            idempotency_key: `${currentCompanyId}:starter_second_site:${week}`,
            payload: { trigger_message: error.message, attempted_name: data.siteName },
            status: "pending",
          }, { onConflict: "idempotency_key", ignoreDuplicates: true });
        }
        return;
      }
      setSyncError(error.message);
      return;
    }

    const websiteId = (insertedWebsite as any).id;
    createdWebsiteRef.current = websiteId;

    // ── Content seeding: auto-link matching articles ──
    if (data.initialArticleCount > 0) {
      try {
        const { data: seedCandidates, error: seedError } = await supabase
          .from("articles")
          .select("id, title, tags, status, updated_at, published_at")
          .order("created_at", { ascending: false })
          .limit(Math.min(Math.max(data.initialArticleCount * 8, 40), 200));

        if (seedError) throw seedError;

        const rankedArticles = ((seedCandidates as SeedCandidate[] | null) || [])
          .map((article) => ({ article, score: scoreSeedCandidate(article, data) }))
          .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return new Date(b.article.updated_at).getTime() - new Date(a.article.updated_at).getTime();
          });

        const matchingArticles = rankedArticles
          .map(({ article }) => article)
          .slice(0, data.initialArticleCount);

        if (matchingArticles && matchingArticles.length > 0) {
          const publications = matchingArticles.map((a: any) => ({
            article_id: a.id,
            website_id: websiteId,
            status: "draft",
          }));
          await supabase.from("article_publications").insert(publications);
          // Update article_count
          await supabase.from("websites").update({ article_count: matchingArticles.length }).eq("id", websiteId);
          toast.success(`${matchingArticles.length} articles linked to ${data.siteName}`);
        }
      } catch (seedErr) {
        console.error("Content seeding error:", seedErr);
        // Non-blocking — continue with provisioning
      }
    }

    setSyncStatus("done");
    toast.success(`${data.siteName} created successfully!`);
    setTimeout(() => navigate("/websites"), 1500);
  };

  const handleRetry = async () => {
    if (createdWebsiteRef.current) {
      navigate(`/websites/${createdWebsiteRef.current}`);
      return;
    }
    await handleLaunch();
  };

  const stepContent: Record<number, React.ReactNode> = {
    1: <StepIdentity d={data} set={set} />,
    2: <StepDesign d={data} set={set} />,
    3: <StepContent d={data} set={set} />,
    4: <StepMonetization d={data} set={set} />,
    5: <StepTechnical d={data} set={set} />,
    6: <StepAI d={data} set={set} />,
    7: <StepAutomation d={data} set={set} />,
    8: <StepReview d={data} />,
  };

  const isBusy = syncStatus === "saving" || syncStatus === "provisioning";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/websites")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="h-4 w-4" />Back
          </button>
          <div className="h-4 w-px bg-border" />
          <div>
            <h1 className="text-lg font-semibold text-foreground">Create Website</h1>
            <p className="text-xs text-muted-foreground">Step {currentStep} of {steps.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.secondaryLangs.length > 0 && (
            <div className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5">
              <Languages className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground">{data.secondaryLangs.length + 1} langs</span>
            </div>
          )}
          {currentStep === steps.length && (
            <button onClick={handleLaunch} disabled={isBusy || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
              {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              {isBusy ? "Creating..." : "Launch Website"}
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 border-r border-border bg-card/30 p-4 shrink-0 overflow-y-auto">
          <div className="space-y-1">
            {steps.map((step) => {
              const active = currentStep === step.id;
              const completed = currentStep > step.id;
              return (
                <button key={step.id} onClick={() => setCurrentStep(step.id)} className={cn("w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all", active && "bg-primary/10 border border-primary/30", completed && !active && "opacity-70", !active && !completed && "hover:bg-accent")}>
                  <div className={cn("flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold shrink-0 transition-colors", active && "bg-primary border-primary text-primary-foreground", completed && !active && "bg-success/20 border-success/40 text-success", !active && !completed && "border-border text-muted-foreground")}>
                    {completed ? <Check className="h-3.5 w-3.5" /> : step.id}
                  </div>
                  <div>
                    <p className={cn("text-sm font-medium", active ? "text-primary" : "text-foreground")}>{step.title}</p>
                    <p className="text-[10px] text-muted-foreground">{step.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 rounded-lg border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">Progress</span>
              <span className="text-xs font-mono text-primary">{Math.round((currentStep / steps.length) * 100)}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${(currentStep / steps.length) * 100}%` }} />
            </div>
          </div>
        </aside>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-8 animate-slide-in" key={currentStep}>
            <div className="flex items-center gap-3 mb-6">
              {(() => {
                const s = steps[currentStep - 1];
                return (
                  <>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10"><s.icon className="h-5 w-5 text-primary" /></div>
                    <div><h2 className="text-xl font-semibold text-foreground">{s.title}</h2><p className="text-sm text-muted-foreground">{s.desc}</p></div>
                  </>
                );
              })()}
            </div>
            {stepContent[currentStep]}
          </div>

          <div className="sticky bottom-0 border-t border-border bg-background/80 backdrop-blur-xl px-8 py-4">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className={cn("flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors", currentStep === 1 ? "text-muted-foreground/40 cursor-not-allowed" : "text-muted-foreground hover:text-foreground border border-border bg-card")}>
                <ChevronLeft className="h-4 w-4" />Previous
              </button>
              <div className="flex items-center gap-1.5">
                {steps.map((s) => (
                  <div key={s.id} className={cn("h-1.5 rounded-full transition-all", s.id === currentStep ? "w-6 bg-primary" : s.id < currentStep ? "w-1.5 bg-primary/50" : "w-1.5 bg-muted")} />
                ))}
              </div>
              <button disabled={isBusy || (currentStep === steps.length && trialMode.isReadOnly)} title={currentStep === steps.length && trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined} onClick={() => { if (currentStep < steps.length) setCurrentStep(currentStep + 1); else handleLaunch(); }} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
                {currentStep === steps.length ? (
                  <>{isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}{isBusy ? "Creating..." : "Launch Website"}</>
                ) : (
                  <>Next<ChevronRight className="h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <SyncStatusBanner status={syncStatus} error={syncError} onRetry={handleRetry} />
    </div>
  );
}
